import {
	ProductScope,
	useGetCommunityProductsQuery,
	useGetProductTagsQuery,
} from '@codezo/graphql';
import { ListFooter, usePagedList, type RowEstimate } from '@codezo/lists';
import { Check, Globe, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProductCard } from '../../components/ProductCard';
import { Card } from '../../components/ui/card';
import { EmptyState } from '../../components/ui/empty-state';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { cn } from '../../lib/utils';
import { useAddToMyProducts } from './useAddToMyProducts';

const PAGE_SIZE = 24;

/**
 * One row of product cards as laid out at the current viewport, for sizing scroll-mode batches.
 * Mirrors the product grid (1/2/3/4 columns at sm/lg/xl, 14px gap, ProductCard's square image
 * + ~73px of text and borders) inside the layout's 212px sidebar (md+), 1440px content cap and
 * the page's 32/48/64px horizontal padding. Shared with the Products page's grid view.
 */
export function productGridRows(): RowEstimate {
	const vw = window.innerWidth;
	const columns = vw >= 1280 ? 4 : vw >= 1024 ? 3 : vw >= 640 ? 2 : 1;
	const sidebar = vw >= 768 ? 212 : 0;
	const padding = vw >= 768 ? 64 : vw >= 640 ? 48 : 32;
	const content = Math.min(vw - sidebar, 1440) - padding;
	const cardWidth = (content - (columns - 1) * 14) / columns;
	return { columns, rowHeight: cardWidth + 73 + 14 };
}

/* ── Tag chip ── */

function TagChip({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			onClick={onClick}
			className={cn(
				'inline-flex h-7 cursor-pointer items-center gap-1 rounded-full border px-3 text-sm transition-colors duration-75',
				active
					? 'border-accent bg-accent-soft text-accent'
					: 'border-line-2 bg-transparent text-text-2 hover:border-line-3 hover:text-text-1'
			)}
		>
			{active && <Check size={11} />}
			{children}
		</button>
	);
}

function GridSkeletons() {
	return (
		<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{Array.from({ length: 8 }).map((_, i) => (
				<Card key={i} className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="animate-pulse bg-surface-fill" style={{ aspectRatio: '1' }} />
					<div className="p-3.5">
						<div className="h-4 w-3/4 animate-pulse rounded bg-surface-fill" />
					</div>
				</Card>
			))}
		</div>
	);
}

/* ════════════════════════ COMMUNITY TAB ════════════════════════ */

/**
 * The Community tab of Products: products our team has published for every client. A product
 * opens on the normal product page (read-only for the client); "Add to my products" copies it
 * into the organization's own products.
 */
export function CommunityTab() {
	const navigate = useNavigate();
	// Search and the paging-mode toggle live in the page's toolbar row; both lists read the same
	// `q`/`page`/`mode` URL params. Only one of them queries at a time (the page skips its own
	// list on this tab) and switching tabs resets search and page, so they never step on each
	// other. Grid only, whatever `view` the My products tab left in the URL.
	const { debouncedSearch, setPage, mode, request, loadMore, limit } = useListParams({
		limit: PAGE_SIZE,
		scrollRows: productGridRows,
	});
	// Selected tag ids, comma-separated in the URL so a filtered view can be shared or revisited.
	const [tagParam, setTagParam] = useQueryParam('tags', '');
	const selectedTags = tagParam ? tagParam.split(',') : [];
	const { requestAdd, addDialog } = useAddToMyProducts();

	const { data: tagData } = useGetProductTagsQuery({
		variables: {
			input: { pagination: { limit: 100, currentPage: 1 }, usedInCommunity: true },
		},
		fetchPolicy: 'no-cache',
	});
	const tags = [...(tagData?.getAllProductTag?.data ?? [])].sort((a, b) =>
		a.name.localeCompare(b.name)
	);

	const { data, loading } = useGetCommunityProductsQuery({
		variables: {
			input: {
				scope: ProductScope.Community,
				pagination: request,
				search: debouncedSearch || undefined,
				tagIds: selectedTags.length ? selectedTags : undefined,
			},
		},
		fetchPolicy: 'no-cache',
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllProduct,
		loading
	);
	const products = list.items;
	const filtered = !!debouncedSearch || selectedTags.length > 0;

	const toggleTag = (id: string) => {
		const next = selectedTags.includes(id)
			? selectedTags.filter((t) => t !== id)
			: [...selectedTags, id];
		setTagParam(next.join(','));
		setPage(1);
	};

	return (
		<>
			{/* ── Tag filter (search lives in the page's toolbar row) ── */}
			{tags.length > 0 && (
				<div className="mb-4 flex flex-wrap gap-1.5">
					<TagChip
						active={selectedTags.length === 0}
						onClick={() => {
							setTagParam('');
							setPage(1);
						}}
					>
						All
					</TagChip>
					{tags.map((tag) => (
						<TagChip
							key={tag.id}
							active={selectedTags.includes(tag.id)}
							onClick={() => toggleTag(tag.id)}
						>
							{tag.name}
						</TagChip>
					))}
				</div>
			)}

			{/* ── Content ── */}
			{list.loading ? (
				<GridSkeletons />
			) : products.length === 0 ? (
				<EmptyState
					icon={Globe}
					title={filtered ? 'Nothing matches' : 'Nothing here yet'}
					description={
						filtered
							? 'Try a different search or fewer tags.'
							: 'Our team has not published any community products yet. Check back soon.'
					}
				/>
			) : (
				<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{products.map((p) => (
						<ProductCard
							key={p.id}
							name={p.name}
							images={p.images}
							tags={p.tags}
							onClick={() => navigate(`/products/${p.id}`)}
							actions={
								p.availableActions.canDuplicate
									? [
											{
												id: 'add',
												label: 'Add to my products',
												icon: Plus,
												onClick: () =>
													requestAdd({ id: p.id, name: p.name }),
											},
										]
									: []
							}
						/>
					))}
				</div>
			)}

			<ListFooter list={list} />

			{addDialog}
		</>
	);
}
